import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Scale, AlertTriangle, CheckCircle, XCircle, ArrowLeft, BookOpen } from 'lucide-react';
import { useSiteSettings } from '../hooks/useSiteSettings';

const TermsOfService = () => {
  const { contactEmail } = useSiteSettings();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-black/95 backdrop-blur-md py-4' : 'bg-transparent py-6'}`}>
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logos/Phoelynx_Transparent.webp"
              alt="Phoelynx France"
              className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
            />
            <span className="lg:text-2xl md:text-xl sm:text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">Phoelynx France</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/" className="hover:text-red-400 transition-colors">ACCUEIL</Link>
            <Link to="/#services" className="hover:text-red-400 transition-colors">SERVICES</Link>
            <Link to="/projets" className="hover:text-red-400 transition-colors">PROJETS</Link>
            <Link to="/#contact" className="hover:text-red-400 transition-colors">CONTACT</Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden">
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-full left-0 w-full bg-black/95 backdrop-blur-md">
            <div className="flex flex-col p-6 space-y-4">
              <Link to="/" className="hover:text-red-400 transition-colors">ACCUEIL</Link>
              <Link to="/#services" className="hover:text-red-400 transition-colors">SERVICES</Link>
              <Link to="/projets" className="hover:text-red-400 transition-colors">PROJETS</Link>
              <Link to="/#contact" className="hover:text-red-400 transition-colors">CONTACT</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center gap-4 mb-6">
            <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-red-600/20 to-sky-600/20">
              <Scale className="w-8 h-8 text-red-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold">Conditions Générales d'Utilisation</h1>
          </div>

          <p className="text-gray-400 text-lg">
            Dernière mise à jour : 1er janvier 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-20 px-6">
        <div className="max-w-4xl mx-auto">

          {/* Introduction */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-red-400" />
              Préambule
            </h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du site web phoelynx.fr
                (ci-après "le Site") exploité par Phoelynx France (ci-après "nous", "notre", "nos").
              </p>
              <p>
                En accédant et en utilisant ce site, vous acceptez d'être lié par ces conditions.
                Si vous n'acceptez pas ces conditions, nous vous demandons de ne pas utiliser ce site.
              </p>
            </div>
          </div>

          {/* Objet */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 1 - Objet</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Les présentes CGU ont pour objet de définir les modalités et conditions d'utilisation des services
                proposés sur le site phoelynx.fr, ainsi que de définir les droits et obligations des parties.
              </p>
              <p>
                Le Site propose les services suivants :
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Présentation des services de formation en robotique</li>
                <li>Information sur les services de développement web</li>
                <li>Conseil en informatique et infrastructure IT</li>
                <li>Organisation de compétitions robotique</li>
                <li>Vente en ligne de matériel informatique et robotique</li>
              </ul>
            </div>
          </div>

          {/* Acceptation */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 2 - Acceptation des conditions</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                L'utilisation du Site implique l'acceptation pleine et entière des présentes CGU.
                Ces dernières constituent un contrat entre l'utilisateur et Phoelynx France.
              </p>
              <div className="bg-red-900/20 p-4 rounded-lg">
                <p className="text-sm flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                  En naviguant sur ce site, vous reconnaissez avoir pris connaissance de ces conditions et les accepter.
                </p>
              </div>
            </div>
          </div>

          {/* Utilisation autorisée */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 3 - Utilisation du site</h2>
            <div className="text-gray-300 space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Utilisations autorisées
                </h3>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Consultation des informations à des fins personnelles</li>
                  <li>Utilisation des formulaires de contact</li>
                  <li>Partage des contenus via les réseaux sociaux</li>
                  <li>Navigation normale sur le site</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  Utilisations interdites
                </h3>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Utilisation à des fins commerciales non autorisées</li>
                  <li>Reproduction ou copie non autorisée du contenu</li>
                  <li>Tentative d'intrusion ou de piratage</li>
                  <li>Diffusion de contenu illégal ou nuisible</li>
                  <li>Utilisation de robots ou scripts automatisés</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Services */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 4 - Services proposés</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Phoelynx France s'efforce de fournir des informations aussi précises que possible.
                Cependant, nous ne pouvons garantir l'exactitude, la complétude ou l'actualité de toutes les informations.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-black/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Formation</h4>
                  <p className="text-sm">Services de formation en robotique, informatique et électronique</p>
                </div>
                <div className="bg-black/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Développement</h4>
                  <p className="text-sm">Création de sites web et applications sur mesure</p>
                </div>
                <div className="bg-black/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Conseil</h4>
                  <p className="text-sm">Expertise en infrastructure IT et administration système</p>
                </div>
                <div className="bg-black/50 p-4 rounded-lg">
                  <h4 className="font-semibold text-white mb-2">Boutique</h4>
                  <p className="text-sm">Vente de matériel informatique et robotique</p>
                </div>
              </div>
            </div>
          </div>

          {/* Responsabilité */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 5 - Responsabilité</h2>
            <div className="text-gray-300 space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-white mb-2">Limitation de responsabilité</h3>
                <p>
                  Phoelynx France ne pourra être tenu responsable des dommages directs ou indirects causés au matériel
                  de l'utilisateur lors de l'accès au site, et résultant soit de l'utilisation d'un matériel ne répondant
                  pas aux spécifications techniques requises, soit de l'apparition d'un bug ou d'une incompatibilité.
                </p>
              </div>

              <div className="bg-orange-900/20 p-4 rounded-lg">
                <p className="text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                  L'utilisateur s'engage à utiliser le site dans le respect des lois en vigueur et des présentes CGU.
                </p>
              </div>
            </div>
          </div>

          {/* Propriété intellectuelle */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 6 - Propriété intellectuelle</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                L'ensemble des éléments du site (textes, images, vidéos, logos, etc.) sont protégés par le droit d'auteur,
                le droit des marques et/ou le droit des dessins et modèles.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication, transmission, dénaturation, totale ou partielle
                du site ou de son contenu, par quelque procédé que ce soit, et sur quelque support que ce soit est interdite.
              </p>
            </div>
          </div>

          {/* Données personnelles */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 7 - Protection des données</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Les données personnelles collectées sur ce site font l'objet d'un traitement informatique destiné
                à répondre à vos demandes et à améliorer nos services.
              </p>
              <div className="text-center">
                <Link to="/politique-confidentialite" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-sky-600 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-red-500/25 transition-all">
                  Consulter notre politique de confidentialité
                </Link>
              </div>
            </div>
          </div>

          {/* Modifications */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 8 - Modifications des CGU</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Phoelynx France se réserve le droit de modifier unilatéralement et à tout moment le contenu de ces CGU.
                Les modifications entrent en vigueur à compter de leur publication sur le site.
              </p>
              <p>
                Il appartient à l'utilisateur de consulter régulièrement les CGU pour prendre connaissance des éventuelles modifications.
              </p>
            </div>
          </div>

          {/* Droit applicable */}
          <div className="mb-12 p-6 border border-gray-800 rounded-xl bg-gray-900/20">
            <h2 className="text-2xl font-bold mb-4">Article 9 - Droit applicable et juridiction</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Les présentes CGU sont régies par le droit français. En cas de litige, et après échec de toute tentative
                de recherche d'une solution amiable, les tribunaux français seront seuls compétents pour connaître de ce litige.
              </p>
              <p>
                Pour toute question relative aux présentes CGU, vous pouvez nous contacter à l'adresse : {contactEmail}
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-3 group">
              <img
                src="/assets/logos/Phoelynx_Transparent.webp"
                alt="Phoelynx France"
                className="w-10 h-10 object-contain transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:brightness-110 drop-shadow-lg group-hover:drop-shadow-xl"
              />
              <div>
                <span className="text-lg font-bold bg-gradient-to-r from-blue-400 via-white to-red-400 bg-clip-text text-transparent">PHOELYNX FRANCE</span>
                <p className="text-xs text-gray-400">Micro-entreprise • SIREN 991540220</p>
              </div>
            </div>

            <div className="flex flex-col items-center md:items-end gap-2">
              <p className="text-gray-400 text-sm">© 2025-{currentYear} Phoelynx France. Tous droits réservés.</p>
              <div className="flex gap-4 text-xs">
                <Link to="/mentions-legales" className="text-gray-500 hover:text-red-400 transition-colors">
                  Mentions légales
                </Link>
                <Link to="/politique-confidentialite" className="text-gray-500 hover:text-red-400 transition-colors">
                  Confidentialité
                </Link>
                <span className="text-red-400">CGU</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;